// Turning platform rows back into the app's own domain shapes. Pure.
//
// WHY THIS EXISTS. The eighteen side-game engines are the most valuable thing in this
// codebase and the most thoroughly tested — several hundred assertions about who owes whom
// when a Nassau is pressed on 14 or a skin carries three times. They take a `RoundCtx`
// built from a `Course` and a `Round`, which is what the phone has locally.
//
// A hosted event does not have those. It has entrants, hole_scores and a `hole_pars` array.
// This maps one onto the other, so a group playing skins inside a tournament settles with
// exactly the same code as a Tuesday fourball — not a second implementation that agrees
// with the first until the day it doesn't.
//
// The alternative would have been reimplementing the engines against the platform's tables,
// in SQL or in the web app. Eighteen games, each with genuine rule subtleties, is not a
// thing worth having two of.

import type { Course, HoleData, HoleSet, Round, RoundPlayer, Tee } from '../types';
import { emptyHole } from '../types';
import type { GameConfig, GameInstance } from '../engine/types';

/** A player in a hosted event, as the platform knows them. */
export type PlatformEntrant = {
  id: string;
  displayName?: string | null;
  courseHandicap?: number | null;
};

/** One recorded score. Absent means the hole was not played. */
export type PlatformScore = {
  entrantId: string;
  holeIdx: number;
  gross: number;
  putts?: number | null;
};

/**
 * Everything a hole carries that is NOT a score: the wolf's pick, a banker's wagers, junk
 * flags, a drive's zone, who took the snake. `HoleData` minus `scores`, verbatim.
 *
 * Five engines read these and see an empty hole without them — a group would score all
 * eighteen and be shown nothing, which is why those games were unofferable on the web card.
 */
export type PlatformHoleInput = {
  holeIdx: number;
  data: Partial<Omit<HoleData, 'scores'>>;
};

export type PlatformRound = {
  id: string;
  startHole?: number | null;
  entrants: PlatformEntrant[];
  scores: PlatformScore[];
  /** The group's registered side games, `GameConfig` verbatim from round_games. */
  games?: Array<{ id: string; config: GameConfig }>;
  /** Per-hole non-score inputs, from round_hole_inputs. */
  holeInputs?: PlatformHoleInput[];
};

export type PlatformCourse = {
  name: string;
  holePars: number[];
  holeStrokeIndexes?: number[] | null;
};

/** The synthetic tee every entrant plays off. */
export const PLATFORM_TEE_ID = 'platform-tee';

/**
 * A Course the engines can read.
 *
 * One tee, because a hosted event records one par and one stroke index per hole for the
 * whole field — nobody is entering four sets of tee data at a registration desk. Yardage is
 * zero rather than invented: no engine reads it, and a made-up number would eventually be
 * shown to somebody as if it meant something.
 *
 * Stroke indexes fall back to hole order when the host never entered them. That only
 * affects WHICH holes carry strokes, not how many, and a defensible order beats refusing to
 * compute — but see `hasRealStrokeIndexes` before showing anything as a net result.
 */
export function courseFromPlatform(c: PlatformCourse): Course {
  const holes = c.holePars.map((par, i) => ({
    par,
    yardage: 0,
    strokeIndex: c.holeStrokeIndexes?.[i] ?? i + 1,
  }));
  const tee: Tee = {
    id: PLATFORM_TEE_ID,
    name: 'Event',
    gender: 'U',
    rating: 72,
    slope: 113,
    yardage: 0,
    holes,
  };
  return { id: 'platform-course', name: c.name, city: '', state: '', tees: [tee] };
}

/**
 * True when the stroke indexes are a real 1..n permutation.
 *
 * `allocateStrokes` treats them as ranks, so a zero or a duplicate quietly hands out an
 * extra stroke on every card in the field rather than failing. Anything that is about to
 * present a NET figure should check this first and fall back to gross if it is false.
 */
export function hasRealStrokeIndexes(si: number[] | null | undefined, holeCount: number): boolean {
  if (!si || si.length !== holeCount) return false;
  const seen = new Set<number>();
  for (const v of si) {
    if (!Number.isInteger(v) || v < 1 || v > holeCount || seen.has(v)) return false;
    seen.add(v);
  }
  return true;
}

/**
 * A Round the engines can read.
 *
 * Entrant ids become player ids, which is what keeps a side-game ledger addressed to the
 * same people the leaderboard and the contests are. Holes are built full-length and filled
 * where scores exist, so an engine asking for hole 14 of an unfinished round gets a null
 * score rather than an index error — every engine already tolerates partial rounds.
 *
 * `holeSet` defaults to 'full', which is exactly right for `courseFromPlatform`'s course:
 * that course's own `tee.holes` is already sliced to the played nine (or the full 18), so
 * there is nothing left for the engine's own front/back slicing (src/engine/holeSet.ts) to
 * do. A hosted round built over a real `CourseCard` course (src/sync/courseCard.ts) is
 * different — that course carries the WHOLE 18 (or 9), so a front/back event needs this set
 * to `'front'`/`'back'` for `playedTeeFor`/`holeGeoForRound` to read the right nine. See
 * `hosted.ts`'s `hostedRoundHoleSet`, which decides which case this is.
 */
export function roundFromPlatform(
  r: PlatformRound,
  holeCount: number,
  holeSet: HoleSet = 'full',
  /** The tee every player starts on. `courseFromPlatform`'s one tee by default; a course
   *  built from a card has real tees, and the round must point at one of THOSE or every
   *  tee lookup falls back to the first tee anyway (engine/handicap.ts `teeFor`). */
  teeId: string = PLATFORM_TEE_ID
): Round {
  const ids = r.entrants.map((e) => e.id);

  const players: RoundPlayer[] = r.entrants.map((e) => ({
    playerId: e.id,
    teeId,
    courseHandicap: e.courseHandicap ?? 0,
  }));

  const holes: HoleData[] = Array.from({ length: holeCount }, () => emptyHole(ids));

  // Non-score inputs go on FIRST, so a malformed stored blob can never clobber a real
  // score. `junk` and `bbb` are merged rather than replaced: emptyHole seeds an entry per
  // player, and a stored blob that only mentions one of them would otherwise leave the
  // others undefined for engines that index them directly.
  for (const input of r.holeInputs ?? []) {
    const hole = holes[input.holeIdx];
    if (!hole || !input.data) continue;
    const { junk, bbb, ...rest } = input.data;
    Object.assign(hole, rest);
    if (junk) hole.junk = { ...hole.junk, ...junk };
    if (bbb) hole.bbb = { ...hole.bbb, ...bbb };
  }

  for (const s of r.scores) {
    const hole = holes[s.holeIdx];
    if (!hole) continue;
    hole.scores[s.entrantId] = { gross: s.gross, putts: s.putts ?? null };
  }

  const games: GameInstance[] = (r.games ?? []).map((g) => ({ id: g.id, config: g.config }));

  return {
    id: r.id,
    courseId: 'platform-course',
    startedAt: 0,
    status: 'active',
    players,
    holes,
    games,
    // Where they teed off, so anything reading currentHole opens in the right place.
    currentHole: r.startHole ?? 0,
    holeSet,
    ...(r.startHole ? { startHole: r.startHole } : {}),
  };
}

/** Net money per entrant across a group's games, in cents. Σ over a group is zero. */
export type MoneyByEntrant = Record<string, number>;

/**
 * Fold a set of computed game results into one net figure per player.
 *
 * Takes results rather than computing them, for the same reason the tournament aggregation
 * does: the caller already holds the engines and the context, and passing results keeps
 * this testable without mounting anything.
 */
export function netMoney(
  results: Array<{ ledger: Array<{ playerId: string; amount: number }> }>
): MoneyByEntrant {
  const out: MoneyByEntrant = {};
  for (const r of results) {
    for (const entry of r.ledger) {
      out[entry.playerId] = (out[entry.playerId] ?? 0) + entry.amount;
    }
  }
  return out;
}

/** "+$4.00" / "−$4.00" / "—". Money is integer cents everywhere in the engine layer. */
export function fmtMoney(cents: number): string {
  if (cents === 0) return '—';
  const sign = cents > 0 ? '+' : '−';
  return `${sign}$${(Math.abs(cents) / 100).toFixed(2)}`;
}
